import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import YhteyshenkilotTab from '../../YhteyshenkilotTab';
import { useReportTemplateContext } from '../ReportTemplate/ReportTemplateContext';

export default function ToimeksiantajaBlock({ data, onChange }) {
  const [fields, setFields] = useState({
    company: '',
    contact: '',
    street: '',
    postalCity: ''
  });

  const {
    rakennusData: kiinteisto,
    handleBlockBlur: onBlur,
  } = useReportTemplateContext();

  const { id: id_kiinteisto } = useParams();

  // -------------------------------
  // Helpers
  // -------------------------------

  const stringifyFields = (obj) => {
    return Object.entries(obj)
      .map(([key, value]) => `${key}:${value ?? ''}`)
      .join('\n');
  };

  const parseFields = (str) => {
    const parsed = {};
    if (!str) return parsed;

    str.split('\n').forEach((line) => {
      const [key, ...rest] = line.split(':');
      if (!key) return;
      parsed[key.trim()] = rest.join(':'); // keep spaces
    });

    return parsed;
  };

  const getDefaults = () => ({
    company: kiinteisto?.kohteen_nimi || '',
    street: kiinteisto?.rakennukset_fulls?.[0]?.osoite || '',
    postalCity: `${kiinteisto?.rakennukset_fulls?.[0]?.postinumero || ''} ${kiinteisto?.rakennukset_fulls?.[0]?.toimipaikka || ''}`.trim()
  });

  // -------------------------------
  // Initialize fields
  // -------------------------------

  useEffect(() => {
    if (!kiinteisto || !kiinteisto.rakennukset_fulls?.length) return;

    let defaults = {
      ...getDefaults(),
      contact: ''
    };

    if (data?.value) {
      const parsed = parseFields(data.value);
      defaults = { ...defaults, ...parsed };
    }

    setFields(defaults);
  }, [kiinteisto, data?.value]);

  // -------------------------------
  // Local editing
  // -------------------------------

  const handleFieldChange = (field, value) => {
    setFields((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Save to parent (called on blur)
  const saveFields = () => {
    onChange({ value: stringifyFields(fields) });
  };

  const restoreDefault = (field) => {
    const defaults = getDefaults();

    const newFields = {
      ...fields,
      [field]: defaults[field] || ''
    };

    setFields(newFields);
    onChange({ value: stringifyFields(newFields) });
  };

  // -------------------------------
  // Contact selection
  // -------------------------------

  const handleSelectContact = (contact) => {
    const newFields = {
      ...fields,
      contact: `c/o ${contact.etunimi} ${contact.sukunimi}${contact.yritys ? ', ' + contact.yritys : ''}`
    };

    setFields(newFields);
    onChange({ value: stringifyFields(newFields) });
  };

  // -------------------------------
  // Loading
  // -------------------------------

  if (!kiinteisto || !kiinteisto.rakennukset_fulls) {
    return <p className="text-muted">Ladataan kiinteistön tietoja...</p>;
  }

  // -------------------------------
  // UI
  // -------------------------------

  return (
    <div className="mb-3">

      <YhteyshenkilotTab
        id_kiinteisto={id_kiinteisto}
        onReport={true}
        onSelectContact={handleSelectContact}
      />

      {/* Company */}
      <div className="mb-2 mt-4">
        <label className="form-label">Yhtiö</label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            value={fields.company}
            onChange={(e) => handleFieldChange('company', e.target.value)}
            onBlur={() => {
              saveFields();
              onBlur?.();
            }}
          />
          <button
            className="btn btn-outline-secondary"
            type="button"
            title="Palauta kiinteistön tiedoista"
            onClick={() => restoreDefault('company')}
          >
            ↺
          </button>
        </div>
      </div>

      {/* Contact */}
      <div className="mb-2">
        <label className="form-label">Yhteyshenkilö</label>
        <input
          type="text"
          className="form-control"
          value={fields.contact}
          onChange={(e) => handleFieldChange('contact', e.target.value)}
          onBlur={() => {
            saveFields();
            onBlur?.();
          }}
        />
      </div>

      {/* Street */}
      <div className="mb-2">
        <label className="form-label">Katuosoite</label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            value={fields.street}
            onChange={(e) => handleFieldChange('street', e.target.value)}
            onBlur={() => {
              saveFields();
              onBlur?.();
            }}
          />
          <button
            className="btn btn-outline-secondary"
            type="button"
            title="Palauta kiinteistön tiedoista"
            onClick={() => restoreDefault('street')}
          >
            ↺
          </button>
        </div>
      </div>

      {/* Postal city */}
      <div className="mb-2">
        <label className="form-label">Postinumero ja kaupunki</label>
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            value={fields.postalCity}
            onChange={(e) => handleFieldChange('postalCity', e.target.value)}
            onBlur={() => {
              saveFields();
              onBlur?.();
            }}
          />
          <button
            className="btn btn-outline-secondary"
            type="button"
            title="Palauta kiinteistön tiedoista"
            onClick={() => restoreDefault('postalCity')}
          >
            ↺
          </button>
        </div>
      </div>

    </div>
  );
}